import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Avatar, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, FormControlLabel, IconButton, InputAdornment, MenuItem,
  Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import { MdAdd, MdArrowBack, MdArrowForward, MdClose, MdPhotoCamera, MdPlayCircle, MdStar, MdStarBorder } from 'react-icons/md';
import RichTextEditor from './RichTextEditor';
import { getData, postData, postFileUpload } from '../../Webservice/Webservice';
import CATEGORY_ROUTES from '../../API_Route/CategoryRoute';
import SUBCATEGORY_ROUTES from '../../API_Route/SubCatagoryRoute';
import {
  BADGES, CONTAINERS, FONT, GST_RATES, HAIR_TYPES, IDEAL_FOR, SECTION_OF, SHIPPING_CLASSES, SKIN_TYPES, UNIT_NAMES, authToken, img, inr, isVideoUrl, validateProduct, yellowBtn,
} from './productShared';

const SECTIONS = [
  { id: 'basics', label: 'Basics' },
  { id: 'media', label: 'Photos & video' },
  { id: 'details', label: 'Details' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'logistics', label: 'Stock & shipping' },
  { id: 'seo', label: 'SEO & maker' },
];

const Section = ({ id, title, hint, accent, children, hasError }) => (
  <Box id={`sec-${id}`} sx={{ background: '#fff', border: '1px solid', borderColor: hasError ? '#fca5a5' : '#e2e8f0', borderTop: `4px solid ${accent}`, borderRadius: '14px', p: { xs: 2, md: 2.75 }, scrollMarginTop: 90 }}>
    <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 16 }}>{title}</Typography>
    {hint && <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b', mb: 2 }}>{hint}</Typography>}
    <Box sx={{ mt: hint ? 0 : 1.5 }}>{children}</Box>
  </Box>
);
const Grid2 = ({ children, cols = 2 }) => (
  <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: `repeat(${cols}, 1fr)` } }}>{children}</Box>
);
const FieldLabel = ({ children, error }) => (
  <Typography sx={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 700, color: error ? '#d32f2f' : '#64748b', mb: 0.75 }}>{children}</Typography>
);

const ProductForm = ({ form, onFormChange, onCancel, onSubmit, isSubmitDisabled = false, categories = [], subcategories = [], isEdit = false, fetchCategories, fetchSubcategories, submitting = false, serverError = '' }) => {
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [titleChecking, setTitleChecking] = useState(false);
  const [titleDup, setTitleDup] = useState(false);
  const [brandOptions, setBrandOptions] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [active, setActive] = useState('basics');
  const [mediaError, setMediaError] = useState('');
  const previews = useRef(new Map());

  const set = (name, value) => onFormChange({ target: { name, value } });
  const on = (name) => (e) => set(name, e.target.value);
  const err = (k) => (submitted ? errors[k] : undefined);
  const isErr = (k) => !!err(k);

  useEffect(() => {
    getData('brand/all', { limit: 200, status: 'active' }, authToken(), false)
      .then((res) => { if (res?.success && Array.isArray(res.brands)) setBrandOptions(res.brands.map((b) => b.name || b)); })
      .catch(() => { });
    getData(CATEGORY_ROUTES.LIST_CATEGORY_CLUSTER, { limit: 1000 }, authToken(), false).then((r) => setClusters(r?.clusters || [])).catch(() => { });
  }, []);

  // keep the error list current once the user has tried to save
  useEffect(() => { setErrors(validateProduct(form)); }, [form]);

  const sectionErrors = useMemo(() => {
    const out = {};
    Object.keys(errors).forEach((k) => { const s = SECTION_OF[k]; if (s) out[s] = (out[s] || 0) + 1; });
    return out;
  }, [errors]);

  // highlight the section in view
  useEffect(() => {
    const els = SECTIONS.map((s) => document.getElementById(`sec-${s.id}`)).filter(Boolean);
    if (!els.length || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => {
      const vis = entries.filter((en) => en.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
      if (vis) setActive(vis.target.id.replace('sec-', ''));
    }, { rootMargin: '-90px 0px -60% 0px' });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const jump = (id) => document.getElementById(`sec-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const submit = () => {
    setSubmitted(true);
    const found = validateProduct(form);
    setErrors(found);
    if (titleDup) found.title = 'This product title already exists.';
    const keys = Object.keys(found);
    if (keys.length) {
      jump(SECTION_OF[keys[0]] || 'basics');
      return;
    }
    onSubmit();
  };

  // ---- title uniqueness ----
  const checkTitle = async () => {
    if (!form.title || !form.title.trim()) return;
    setTitleChecking(true);
    try {
      const res = await postData('product/check-duplicate-title', { title: form.title, id: isEdit ? form._id || null : null }, authToken(), false);
      setTitleDup(!!res?.exists);
    } catch { /* the save will catch a duplicate anyway */ } finally { setTitleChecking(false); }
  };

  // ---- media ----
  const media = Array.isArray(form.product_images) ? form.product_images : [];
  const urlOf = (m) => {
    if (typeof m === 'string') return img(m);
    if (!previews.current.has(m)) previews.current.set(m, URL.createObjectURL(m));
    return previews.current.get(m);
  };
  const isVid = (m) => (m instanceof File ? m.type.startsWith('video/') : isVideoUrl(m));
  const addFiles = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    setMediaError('');
    const ok = [];
    for (const f of files) {
      if (f.type.startsWith('image/')) {
        if (f.size > 5 * 1024 * 1024) { setMediaError(`“${f.name}” is over 5 MB.`); continue; }
        ok.push(f);
      } else if (f.type.startsWith('video/')) {
        if (f.size > 60 * 1024 * 1024) { setMediaError(`“${f.name}” is over 60 MB.`); continue; }
        ok.push(f);
      } else setMediaError(`“${f.name}” is not an image or video.`);
    }
    if (ok.length) set('product_images', [...media, ...ok]);
  };
  const removeMedia = (i) => set('product_images', media.filter((_, j) => j !== i));
  const moveMedia = (i, dir) => {
    let j = i + dir;
    while (j >= 0 && j < media.length && isVid(media[j]) !== isVid(media[i])) j += dir;
    if (j < 0 || j >= media.length) return;
    const next = [...media];
    [next[i], next[j]] = [next[j], next[i]];
    set('product_images', next);
  };
  const canMove = (i, dir) => {
    let j = i + dir;
    while (j >= 0 && j < media.length && isVid(media[j]) !== isVid(media[i])) j += dir;
    return j >= 0 && j < media.length;
  };
  const makeCover = (i) => { const m = media[i]; set('product_images', [m, ...media.filter((_, j) => j !== i)]); };
  const firstImageIndex = media.findIndex((m) => !isVid(m));

  // ---- visibility ----
  const vis = Array.isArray(form.visibility) ? form.visibility : [];
  const toggleVis = (k) => set('visibility', vis.includes(k) ? vis.filter((x) => x !== k) : [...vis, k]);

  // ---- quick add category / subcategory ----
  const [catOpen, setCatOpen] = useState(false);
  const [newCat, setNewCat] = useState({ title: '', cluster: '', file: null, error: '' });
  const [subOpen, setSubOpen] = useState(false);
  const [newSub, setNewSub] = useState({ title: '', file: null, error: '' });
  const [quickSaving, setQuickSaving] = useState(false);

  const saveCategory = async () => {
    if (!newCat.title.trim()) return setNewCat({ ...newCat, error: 'Enter a name' });
    if (!newCat.cluster) return setNewCat({ ...newCat, error: 'Choose a cluster' });
    if (!newCat.file) return setNewCat({ ...newCat, error: 'Upload an image' });
    setQuickSaving(true);
    try {
      const body = new FormData();
      body.append('title', newCat.title.trim());
      body.append('status', 'active');
      body.append('categoryCluster', newCat.cluster);
      body.append('image', newCat.file);
      const created = await postFileUpload(CATEGORY_ROUTES.ADD_CATEGORY, body, authToken(), false);
      if (fetchCategories) await fetchCategories();
      if (created?._id) set('category_id', created._id);
      setCatOpen(false);
      setNewCat({ title: '', cluster: '', file: null, error: '' });
    } catch (e2) {
      setNewCat((n) => ({ ...n, error: e2?.message || 'Could not add the category' }));
    } finally { setQuickSaving(false); }
  };
  const saveSubcategory = async () => {
    if (!form.category_id) return setNewSub({ ...newSub, error: 'Choose a category first' });
    if (!newSub.title.trim()) return setNewSub({ ...newSub, error: 'Enter a name' });
    if (!newSub.file) return setNewSub({ ...newSub, error: 'Upload an image' });
    setQuickSaving(true);
    try {
      const body = new FormData();
      body.append('title', newSub.title.trim());
      body.append('status', 'active');
      body.append('category_id', form.category_id);
      body.append('image', newSub.file);
      const created = await postFileUpload(SUBCATEGORY_ROUTES.ADD_SUBCATEGORY, body, authToken(), false);
      if (fetchSubcategories) await fetchSubcategories(form.category_id);
      if (created?._id) set('subcategory_id', created._id);
      setSubOpen(false);
      setNewSub({ title: '', file: null, error: '' });
    } catch (e2) {
      setNewSub((n) => ({ ...n, error: e2?.message || 'Could not add the subcategory' }));
    } finally { setQuickSaving(false); }
  };

  const discount = (mrp, selling) => (Number(mrp) > 0 && Number(selling) > 0 && Number(selling) < Number(mrp) ? Math.round(((mrp - selling) / mrp) * 100) : 0);
  const cover = media.find((m) => !isVid(m));

  const richBox = (key, label, hint) => (
    <Box>
      <FieldLabel error={isErr(key)}>{label}</FieldLabel>
      <Box sx={{ border: '1px solid', borderColor: isErr(key) ? '#d32f2f' : '#cbd5e1', borderRadius: '10px', overflow: 'hidden', '& .ql-toolbar': { border: 'none', borderBottom: '1px solid #e2e8f0' }, '& .ql-container': { border: 'none', fontFamily: FONT }, '& .ql-editor': { minHeight: 110 } }}>
        <RichTextEditor value={form[key] || ''} onChange={(content) => { if (content !== (form[key] || '')) set(key, content); }} placeholder={hint} />
      </Box>
      {isErr(key) && <Typography sx={{ color: '#d32f2f', fontSize: 12.5, mt: 0.5 }}>{err(key)}</Typography>}
    </Box>
  );

  const textField = (name, label, extra = {}) => (
    <TextField size="small" fullWidth label={label} value={form[name] ?? ''} onChange={on(name)} error={isErr(name)} helperText={err(name) || extra.helper} {...extra.props} />
  );

  return (
    <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', lg: '210px 1fr' }, alignItems: 'start', fontFamily: FONT }}>
      {/* section navigation */}
      <Box sx={{ position: { lg: 'sticky' }, top: 80, display: { xs: 'none', lg: 'flex' }, flexDirection: 'column', gap: 0.5, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', p: 1.25 }}>
        {SECTIONS.map((s) => (
          <Box key={s.id} onClick={() => jump(s.id)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', px: 1.5, py: 1, borderRadius: '10px', background: active === s.id ? 'rgb(255 209 2 / 25%)' : 'transparent', '&:hover': { background: 'rgb(255 209 2 / 18%)' } }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: active === s.id ? 800 : 600, fontSize: 13.5 }}>{s.label}</Typography>
            {submitted && sectionErrors[s.id] ? <Chip size="small" label={sectionErrors[s.id]} sx={{ height: 20, fontWeight: 800, background: '#fee2e2', color: '#991b1b' }} /> : null}
          </Box>
        ))}
        {cover && (
          <Box sx={{ mt: 1, p: 1, borderTop: '1px solid #f1f5f9' }}>
            <Avatar variant="rounded" src={urlOf(cover)} sx={{ width: '100%', height: 130, borderRadius: '10px' }} />
            <Typography noWrap sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13, mt: 1 }}>{form.title || 'Untitled product'}</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>{form.isFree ? 'Free product' : `${inr(form.b2c_sellingPrice)}${discount(form.b2c_mrp, form.b2c_sellingPrice) ? ` · ${discount(form.b2c_mrp, form.b2c_sellingPrice)}% off` : ''}`}</Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, minWidth: 0 }}>
        {/* BASICS */}
        <Section id="basics" title="Basics" hint="What it is and where it belongs" accent="#6366f1" hasError={submitted && !!sectionErrors.basics}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField size="small" fullWidth label="Product title *" value={form.title || ''} onChange={(e) => { setTitleDup(false); set('title', e.target.value); }} onBlur={checkTitle}
              error={isErr('title') || titleDup} helperText={titleDup ? 'This product title already exists.' : err('title') || (titleChecking ? 'Checking the title…' : ' ')}
              InputProps={{ endAdornment: titleChecking ? <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment> : null }} />
            <TextField size="small" fullWidth multiline rows={3} label="Short description *" value={form.description || ''} onChange={on('description')} error={isErr('description')} helperText={err('description')} />
            <Grid2>
              <Autocomplete freeSolo options={brandOptions} value={form.brand || ''} inputValue={form.brand || ''} onInputChange={(_, v) => set('brand', v || '')} onChange={(_, v) => set('brand', v || '')}
                renderInput={(params) => <TextField {...params} size="small" label="Brand *" error={isErr('brand')} helperText={err('brand') || (form.brand && !brandOptions.some((b) => b.toLowerCase() === form.brand.toLowerCase()) ? 'Not in the brand list yet. It will still be saved.' : ' ')} />} />
              {textField('upc', 'UPC / barcode', { helper: ' ' })}
            </Grid2>
            <Grid2>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField select size="small" fullWidth label="Category *" value={form.category_id || ''} onChange={on('category_id')} error={isErr('category_id')} helperText={err('category_id') || ' '}>
                  {categories.map((c) => <MenuItem key={c._id} value={c._id}>{c.title}{c.status !== 'active' ? ' (inactive)' : ''}</MenuItem>)}
                </TextField>
                <Tooltip title="Add a category"><IconButton onClick={() => setCatOpen(true)} sx={{ mt: 0.25, border: '1px solid #cbd5e1', borderRadius: '10px' }}><MdAdd size={18} /></IconButton></Tooltip>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField select size="small" fullWidth label="Subcategory *" value={form.subcategory_id || ''} onChange={on('subcategory_id')} disabled={!form.category_id} error={isErr('subcategory_id')} helperText={err('subcategory_id') || (form.category_id ? ' ' : 'Choose a category first')}>
                  {subcategories.map((s) => <MenuItem key={s._id} value={s._id}>{s.title}{s.status !== 'active' ? ' (inactive)' : ''}</MenuItem>)}
                </TextField>
                <Tooltip title="Add a subcategory"><span><IconButton disabled={!form.category_id} onClick={() => setSubOpen(true)} sx={{ mt: 0.25, border: '1px solid #cbd5e1', borderRadius: '10px' }}><MdAdd size={18} /></IconButton></span></Tooltip>
              </Box>
            </Grid2>
            <Grid2>
              {textField('hsn_code', 'HSN code')}
              <TextField size="small" fullWidth label="GSIN" value={form.gsin || (isEdit ? '' : 'Generated when saved')} disabled helperText="System generated" />
            </Grid2>
            <Autocomplete multiple freeSolo options={[]} value={Array.isArray(form.tags) ? form.tags : []} onChange={(_, v) => set('tags', v)}
              renderTags={(value, getTagProps) => value.map((o, i) => <Chip size="small" label={o} {...getTagProps({ index: i })} key={o + i} />)}
              renderInput={(params) => <TextField {...params} size="small" label="Search keywords" placeholder="Type and press Enter" />} />
            <Box>
              <FieldLabel error={isErr('visibility')}>Who can buy it</FieldLabel>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <FormControlLabel control={<Checkbox checked={vis.includes('b2c')} onChange={() => toggleVis('b2c')} />} label="B2C customers" />
                <FormControlLabel control={<Checkbox checked={vis.includes('b2b')} onChange={() => toggleVis('b2b')} />} label="B2B customers" />
                <FormControlLabel control={<Switch checked={!!form.codAvailable} onChange={(e) => set('codAvailable', e.target.checked)} />} label="Cash on delivery" />
                <FormControlLabel control={<Switch checked={!!form.isPublished} onChange={(e) => set('isPublished', e.target.checked)} />} label="Live on the storefront" />
              </Box>
              {isErr('visibility') && <Typography sx={{ color: '#d32f2f', fontSize: 12.5 }}>{err('visibility')}</Typography>}
            </Box>
            <TextField select size="small" label="Approval status" value={form.status || 'approved'} onChange={on('status')} sx={{ maxWidth: 260 }} helperText="Only approved products can go live">
              <MenuItem value="approved">Approved</MenuItem><MenuItem value="pending">Pending</MenuItem><MenuItem value="rejected">Rejected</MenuItem>
            </TextField>
          </Box>
        </Section>

        {/* MEDIA */}
        <Section id="media" title="Photos & video" hint="The first photo is the primary image. Use the arrows to swap pictures into the order customers see them, or press Set primary under a photo. Photos up to 5 MB; videos up to 30 seconds." accent="#ec4899" hasError={submitted && !!sectionErrors.media}>
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))' }}>
            {media.map((m, i) => (
              <Box key={typeof m === 'string' ? m : `${m.name}-${i}`} sx={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', border: '2px solid', borderColor: i === firstImageIndex ? '#f59e0b' : '#e2e8f0', background: '#f8fafc', aspectRatio: '1 / 1' }}>
                {isVid(m) ? (
                  <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
                    <video src={urlOf(m)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted />
                    <MdPlayCircle size={34} color="#fff" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,.6))' }} />
                  </Box>
                ) : <img src={urlOf(m)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                {i === firstImageIndex && <Chip size="small" label="Primary" sx={{ position: 'absolute', left: 6, top: 6, height: 20, fontWeight: 800, background: '#f59e0b', color: '#fff' }} />}
                <Box sx={{ position: 'absolute', right: 4, top: 4, display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Remove"><IconButton size="small" onClick={() => removeMedia(i)} sx={{ background: 'rgba(255,255,255,.9)', p: 0.4 }}><MdClose size={16} /></IconButton></Tooltip>
                </Box>
                <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 0.25, background: 'rgba(255,255,255,.92)' }}>
                  <Tooltip title="Move earlier"><span><IconButton size="small" disabled={!canMove(i, -1)} onClick={() => moveMedia(i, -1)} sx={{ p: 0.4 }}><MdArrowBack size={15} /></IconButton></span></Tooltip>
                  {isVid(m) ? (
                    <Typography sx={{ fontFamily: FONT, fontSize: 10.5, fontWeight: 700, color: '#64748b' }}>Video</Typography>
                  ) : i === firstImageIndex ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color: '#b45309', fontFamily: FONT, fontSize: 10.5, fontWeight: 800 }}><MdStar size={14} /></Box>
                  ) : (
                    <Tooltip title="Use this as the primary image"><Box component="button" type="button" onClick={() => makeCover(i)} sx={{ display: 'flex', alignItems: 'center', gap: 0.25, border: 0, background: 'transparent', cursor: 'pointer', color: '#475569', fontFamily: FONT, fontSize: 10.5, fontWeight: 700, p: 0.25, borderRadius: '6px', '&:hover': { background: '#fef3c7', color: '#b45309' } }}><MdStarBorder size={14} /></Box></Tooltip>
                  )}
                  <Tooltip title="Move later"><span><IconButton size="small" disabled={!canMove(i, 1)} onClick={() => moveMedia(i, 1)} sx={{ p: 0.4 }}><MdArrowForward size={15} /></IconButton></span></Tooltip>
                </Box>
              </Box>
            ))}
            <Button component="label" sx={{ aspectRatio: '1 / 1', border: '2px dashed', borderColor: isErr('product_images') ? '#d32f2f' : '#cbd5e1', borderRadius: '12px', flexDirection: 'column', gap: 0.5, textTransform: 'none', fontWeight: 700, color: '#475569' }}>
              <MdPhotoCamera size={26} />Add photos / video
              <input hidden multiple type="file" accept="image/*,video/*" onChange={addFiles} />
            </Button>
          </Box>
          {(mediaError || isErr('product_images')) && <Typography sx={{ color: '#d32f2f', fontSize: 12.5, mt: 1 }}>{mediaError || err('product_images')}</Typography>}
        </Section>

        {/* DETAILS */}
        <Section id="details" title="Details" hint="What customers read before buying" accent="#0ea5e9" hasError={submitted && !!sectionErrors.details}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {richBox('key_features', 'Key features *', 'Bullet the main selling points')}
            {richBox('how_to_use', 'How to use *', 'Step-by-step directions')}
            <Grid2 cols={3}>
              <TextField select size="small" fullWidth label="Unit type *" value={form.unitType || ''} onChange={on('unitType')} error={isErr('unitType')} helperText={err('unitType') || ' '}>
                <MenuItem value="unit">Unit (count)</MenuItem><MenuItem value="weight">Weight / volume</MenuItem>
              </TextField>
              <TextField size="small" fullWidth type="number" label="Pack size *" value={form.unitValue ?? ''} onChange={on('unitValue')} error={isErr('unitValue')} helperText={err('unitValue') || ' '} inputProps={{ min: 0 }} />
              <TextField select size="small" fullWidth label={form.unitType === 'weight' ? 'Unit *' : 'Unit'} value={form.unitName || ''} onChange={on('unitName')} disabled={form.unitType !== 'weight'} error={isErr('unitName')} helperText={err('unitName') || ' '}>
                {UNIT_NAMES.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
              </TextField>
            </Grid2>
            <Grid2 cols={3}>
              <TextField select size="small" fullWidth label="Ideal for *" value={form.ideal_for || ''} onChange={on('ideal_for')} error={isErr('ideal_for')} helperText={err('ideal_for')}>
                {IDEAL_FOR.map((u) => <MenuItem key={u} value={u} sx={{ textTransform: 'capitalize' }}>{u}</MenuItem>)}
              </TextField>
              <TextField select size="small" fullWidth label="Container *" value={form.container_type || ''} onChange={on('container_type')} error={isErr('container_type')} helperText={err('container_type')}>
                {CONTAINERS.map((u) => <MenuItem key={u} value={u} sx={{ textTransform: 'capitalize' }}>{u.replace(/_/g, ' ')}</MenuItem>)}
              </TextField>
              {textField('applied_for', 'Applied for')}
            </Grid2>
            <Grid2 cols={3}>
              {textField('country_of_orgin', 'Country of origin')}
              {textField('color', 'Colour')}
              <TextField select size="small" fullWidth label="Badge" value={form.badge || ''} onChange={on('badge')}>
                <MenuItem value="">None</MenuItem>{BADGES.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
              </TextField>
            </Grid2>
            <Grid2 cols={3}>
              <TextField select size="small" fullWidth label="Skin type" value={form.skin_type || ''} onChange={on('skin_type')}>
                <MenuItem value="">Not specified</MenuItem>{SKIN_TYPES.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
              </TextField>
              <TextField select size="small" fullWidth label="Hair type" value={form.hair_type || ''} onChange={on('hair_type')}>
                <MenuItem value="">Not specified</MenuItem>{HAIR_TYPES.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
              </TextField>
              {textField('fragrance_notes', 'Fragrance notes')}
            </Grid2>
            <TextField size="small" fullWidth multiline rows={2} label="Ingredients" value={form.ingredients || ''} onChange={on('ingredients')} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <FormControlLabel control={<Switch checked={!!form.is_featured} onChange={(e) => set('is_featured', e.target.checked)} />} label="Featured" />
              <FormControlLabel control={<Switch checked={!!form.is_vegan} onChange={(e) => set('is_vegan', e.target.checked)} />} label="Vegan" />
              <FormControlLabel control={<Switch checked={!!form.cruelty_free} onChange={(e) => set('cruelty_free', e.target.checked)} />} label="Cruelty free" />
              <FormControlLabel control={<Switch checked={!!form.organic} onChange={(e) => set('organic', e.target.checked)} />} label="Organic" />
            </Box>
          </Box>
        </Section>

        {/* PRICING */}
        <Section id="pricing" title="Pricing" hint="Selling prices follow the MRP and discount; you can still type a price" accent="#22c55e" hasError={submitted && !!sectionErrors.pricing}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Grid2>
              <TextField select size="small" fullWidth label="GST rate *" value={form.gst || ''} onChange={on('gst')} error={isErr('gst')} helperText={err('gst') || ' '}>
                {GST_RATES.map((g) => <MenuItem key={g} value={g}>{g}%</MenuItem>)}
              </TextField>
              <FormControlLabel control={<Switch checked={!!form.isFree} onChange={(e) => set('isFree', e.target.checked)} />} label="Free gift product" sx={{ alignSelf: 'center' }} />
            </Grid2>
            {form.isFree ? (
              <Grid2>
                <TextField size="small" fullWidth type="number" inputProps={{ min: 0 }} label="Cart amount that unlocks it *" value={form.thresholdAmount ?? ''} onChange={on('thresholdAmount')} error={isErr('thresholdAmount')} helperText={err('thresholdAmount')} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
                <FormControlLabel control={<Switch checked={!!form.autoAdd} onChange={(e) => set('autoAdd', e.target.checked)} />} label="Add to the cart automatically" sx={{ alignSelf: 'center' }} />
              </Grid2>
            ) : (
              <>
                <TextField size="small" label="MRP *" type="number" value={form.b2c_mrp ?? ''} onChange={on('b2c_mrp')} error={isErr('b2c_mrp')} helperText={err('b2c_mrp')} sx={{ maxWidth: 280 }} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} inputProps={{ min: 0 }} />
                {[['b2c', 'B2C customers'], ['b2b', 'B2B customers']].map(([k, label]) => (
                  <Box key={k} sx={{ p: 2, borderRadius: '12px', background: '#f8fafc', border: '1px solid #e2e8f0', opacity: vis.includes(k) ? 1 : 0.55 }}>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14, mb: 1.25 }}>{label}{!vis.includes(k) && <span style={{ fontWeight: 600, color: '#94a3b8' }}> · not visible to them</span>}</Typography>
                    <Grid2 cols={3}>
                      <TextField select size="small" fullWidth label="Discount type" value={form[`${k}_discountType`] || ''} onChange={on(`${k}_discountType`)}>
                        <MenuItem value="">No discount</MenuItem><MenuItem value="percentage">Percentage</MenuItem><MenuItem value="flat">Flat amount</MenuItem>
                      </TextField>
                      <TextField size="small" fullWidth type="number" label="Discount" value={form[`${k}_discountValue`] ?? ''} onChange={on(`${k}_discountValue`)} disabled={!form[`${k}_discountType`]} error={isErr(`${k}_discountValue`)} helperText={err(`${k}_discountValue`)} inputProps={{ min: 0 }} />
                      <TextField size="small" fullWidth type="number" inputProps={{ min: 0 }} label="Selling price *" value={form[`${k}_sellingPrice`] ?? ''} onChange={on(`${k}_sellingPrice`)} error={isErr(`${k}_sellingPrice`)} helperText={err(`${k}_sellingPrice`) || (discount(form.b2c_mrp, form[`${k}_sellingPrice`]) ? `${discount(form.b2c_mrp, form[`${k}_sellingPrice`])}% below MRP` : ' ')} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
                    </Grid2>
                  </Box>
                ))}
              </>
            )}
          </Box>
        </Section>

        {/* LOGISTICS */}
        <Section id="logistics" title="Stock & shipping" hint="Live stock comes from your warehouses and cannot be edited here" accent="#f59e0b" hasError={submitted && !!sectionErrors.logistics}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Grid2 cols={3}>
              <TextField size="small" fullWidth type="number" label="Low-stock alert at" value={form.minimumStockQuantity ?? ''} onChange={on('minimumStockQuantity')} helperText="Flagged low at or below this" inputProps={{ min: 0 }} />
              <TextField select size="small" fullWidth label="Shipping class" value={form.shipping_class || ''} onChange={on('shipping_class')}>
                <MenuItem value="">Not specified</MenuItem>{SHIPPING_CLASSES.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
              </TextField>
              <TextField size="small" fullWidth type="number" label="Return window (days)" value={form.return_policy ?? 0} onChange={on('return_policy')} error={isErr('return_policy')} helperText={err('return_policy') || '0 means no returns'} inputProps={{ min: 0 }} />
            </Grid2>
            <Box>
              <FieldLabel>Size and weight of the pack</FieldLabel>
              <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(6, 1fr)' } }}>
                <TextField size="small" type="number" label="Length" value={form.length ?? ''} onChange={on('length')} inputProps={{ min: 0 }} />
                <TextField size="small" type="number" label="Width" value={form.width ?? ''} onChange={on('width')} inputProps={{ min: 0 }} />
                <TextField size="small" type="number" label="Height" value={form.height ?? ''} onChange={on('height')} inputProps={{ min: 0 }} />
                <TextField select size="small" label="Unit" value={form.dimensionUnit || ''} onChange={on('dimensionUnit')}>
                  <MenuItem value="">—</MenuItem><MenuItem value="cm">cm</MenuItem><MenuItem value="in">in</MenuItem><MenuItem value="mm">mm</MenuItem>
                </TextField>
                <TextField size="small" type="number" label="Weight" value={form.weight ?? ''} onChange={on('weight')} inputProps={{ min: 0 }} />
                <TextField select size="small" label="Weight unit" value={form.weightUnit || ''} onChange={on('weightUnit')}>
                  <MenuItem value="">—</MenuItem><MenuItem value="g">g</MenuItem><MenuItem value="kg">kg</MenuItem><MenuItem value="lb">lb</MenuItem>
                </TextField>
              </Box>
            </Box>
            {isEdit && (
              <Alert severity="info">Available quantity is {form.availableQuantity ?? 0}. It changes when stock is received or sold, so it is not part of this form.</Alert>
            )}
          </Box>
        </Section>

        {/* SEO */}
        <Section id="seo" title="SEO & manufacturer" hint="Optional, but helps search and compliance" accent="#8b5cf6" hasError={submitted && !!sectionErrors.seo}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Grid2>
              {textField('slug', 'Web address (slug)', { helper: 'e.g. rose-face-wash-200ml' })}
              {textField('meta_title', 'Meta title')}
            </Grid2>
            <TextField size="small" fullWidth multiline rows={2} label="Meta description" value={form.meta_description || ''} onChange={on('meta_description')} helperText={`${(form.meta_description || '').length}/160 recommended`} />
            <Grid2 cols={3}>
              {textField('manufacturerName', 'Manufacturer')}
              {textField('manufacturingLicenseNumber', 'Licence number')}
              {textField('manufacturerAddress', 'Address')}
            </Grid2>
          </Box>
        </Section>

        {(serverError || (submitted && Object.keys(errors).length > 0)) && (
          <Alert severity="error">
            {serverError || `Please fix ${Object.keys(errors).length} thing${Object.keys(errors).length === 1 ? '' : 's'} before saving: ${Object.keys(errors).slice(0, 4).map((k) => errors[k]).join(' · ')}${Object.keys(errors).length > 4 ? ' …' : ''}`}
          </Alert>
        )}

        <Box sx={{ position: 'sticky', bottom: 0, zIndex: 5, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, p: 1.75, background: 'rgba(255,255,255,.96)', borderTop: '1px solid #e2e8f0', borderRadius: '14px', boxShadow: '0 -6px 18px rgba(15,23,42,.06)' }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b', mr: 'auto' }}>{isEdit ? 'Editing' : 'New product'}{form.title ? ` · ${form.title}` : ''}</Typography>
          <Button onClick={onCancel} disabled={submitting} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={submit} variant="contained" disabled={submitting || isSubmitDisabled} sx={yellowBtn}>{submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Create product'}</Button>
        </Box>
      </Box>

      {/* quick add category */}
      <Dialog open={catOpen} onClose={() => !quickSaving && setCatOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogContent sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 18 }}>Add a category</Typography>
          <TextField size="small" label="Name *" value={newCat.title} onChange={(e) => setNewCat({ ...newCat, title: e.target.value, error: '' })} autoFocus />
          <TextField select size="small" label="Cluster *" value={newCat.cluster} onChange={(e) => setNewCat({ ...newCat, cluster: e.target.value, error: '' })}>
            {clusters.map((c) => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
          </TextField>
          <Button component="label" startIcon={<MdPhotoCamera />} sx={{ textTransform: 'none', fontWeight: 700, border: '1px solid #cbd5e1', borderRadius: '10px', color: '#0f172a' }}>
            {newCat.file ? newCat.file.name : 'Upload image *'}<input hidden type="file" accept="image/*" onChange={(e) => setNewCat({ ...newCat, file: e.target.files?.[0] || null, error: '' })} />
          </Button>
          {newCat.error && <Alert severity="error">{newCat.error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCatOpen(false)} disabled={quickSaving} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={saveCategory} disabled={quickSaving} variant="contained" sx={yellowBtn}>{quickSaving ? 'Adding…' : 'Add'}</Button>
        </DialogActions>
      </Dialog>

      {/* quick add subcategory */}
      <Dialog open={subOpen} onClose={() => !quickSaving && setSubOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogContent sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 18 }}>Add a subcategory</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>In “{categories.find((c) => c._id === form.category_id)?.title || 'the chosen category'}”</Typography>
          <TextField size="small" label="Name *" value={newSub.title} onChange={(e) => setNewSub({ ...newSub, title: e.target.value, error: '' })} autoFocus />
          <Button component="label" startIcon={<MdPhotoCamera />} sx={{ textTransform: 'none', fontWeight: 700, border: '1px solid #cbd5e1', borderRadius: '10px', color: '#0f172a' }}>
            {newSub.file ? newSub.file.name : 'Upload image *'}<input hidden type="file" accept="image/*" onChange={(e) => setNewSub({ ...newSub, file: e.target.files?.[0] || null, error: '' })} />
          </Button>
          {newSub.error && <Alert severity="error">{newSub.error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSubOpen(false)} disabled={quickSaving} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={saveSubcategory} disabled={quickSaving} variant="contained" sx={yellowBtn}>{quickSaving ? 'Adding…' : 'Add'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductForm;
